import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import * as React from "react";
import {
  highlightTokensInText,
  type MatchRange,
  type SearchableField,
  type SearchResult,
  smartSearch,
  splitByMatches,
} from "@/shared/utils/smartSearch";
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
} from "@/v2/components/ui/popover";
import { useIsMobile } from "@/v2/hooks/useIsMobile";
import { cn } from "@/v2/lib/utils";

// ── Types ──────────────────────────────────────────────────────────

export interface ComboBoxProps<T> {
  items: T[];
  value: string | null;
  onChange: (value: string) => void;
  /** Search field extractors passed to smartSearch */
  fields: ((item: T) => SearchableField)[];
  /** Unique key for each item */
  getKey: (item: T) => string;
  /** Display label when item is selected (shown in the input) */
  getDisplayLabel: (item: T) => string;
  /** Render a single option row. Receives match ranges and tokens for highlighting. */
  renderItem?: (
    item: T,
    highlighted: boolean,
    matches: Map<number, MatchRange[]>,
    tokens?: string[],
  ) => React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  disabledMessage?: string;
  error?: string;
  className?: string;
  onBlur?: () => void;
  /** Max results to show (default 50) */
  limit?: number;
  /** Max items to show when there's no search query (defaults to `limit`). */
  defaultLimit?: number;
  /** Show clear (X) button when a value is selected */
  clearable?: boolean;
  onClear?: () => void;
  /** Text to pre-fill when the input is focused via Tab */
  getSearchValue?: (item: T) => string;
  /** Render a rich display inside the input when an item is selected */
  renderSelected?: (item: T) => React.ReactNode;
  /** Width of the dropdown (default: match input width) */
  dropdownWidth?: string;
  /** Override the items shown when the query is empty. Defaults to all items (capped by limit). */
  getDefaultItems?: (items: T[]) => T[];
  /** Override the search-with-query behavior. When set, replaces smartSearch. */
  searchItems?: (items: T[], query: string) => T[];
  /**
   * Fallback used to render the *selected* value when it isn't present in
   * `items` yet — e.g. the full option list is still loading over the network.
   * Display-only: it never appears in the dropdown or search results.
   */
  selectedItemFallback?: T | null;
}

// ── Component ──────────────────────────────────────────────────────

export function ComboBox<T>({
  items,
  value,
  onChange,
  fields,
  getKey,
  getDisplayLabel,
  renderItem,
  placeholder = "Search...",
  disabled = false,
  disabledMessage,
  error,
  className,
  onBlur,
  limit = 50,
  defaultLimit,
  clearable = false,
  onClear,
  getSearchValue,
  renderSelected,
  dropdownWidth,
  getDefaultItems,
  searchItems,
  selectedItemFallback,
}: ComboBoxProps<T>) {
  const isMobile = useIsMobile();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const listRef = React.useRef<HTMLUListElement>(null);
  const pointerFocusRef = React.useRef(false);

  const initialCap = defaultLimit ?? limit;

  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const [visibleCount, setVisibleCount] = React.useState(initialCap);

  // Mobile full-screen sheet state
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [mobileInputValue, setMobileInputValue] = React.useState("");

  // Find selected item
  const selectedItem = React.useMemo(() => {
    if (!value) return null;
    const found = items.find((item) => getKey(item) === value);
    if (found) return found;
    return selectedItemFallback && getKey(selectedItemFallback) === value
      ? selectedItemFallback
      : null;
  }, [items, value, getKey, selectedItemFallback]);

  const displayText = selectedItem ? getDisplayLabel(selectedItem) : "";

  // Search results (used by both modes)
  const currentQuery = isMobile && mobileOpen ? mobileInputValue : inputValue;

  React.useEffect(() => {
    setVisibleCount(initialCap);
  }, [currentQuery, items, initialCap]);

  const { results, totalAvailable } = React.useMemo<{
    results: SearchResult<T>[];
    totalAvailable: number;
  }>(() => {
    const wrap = (arr: T[]): SearchResult<T>[] =>
      arr.map((item) => ({
        item,
        score: 0,
        matches: new Map<number, MatchRange[]>(),
        matchedTokens: [] as string[],
      }));

    if (!currentQuery.trim()) {
      const base = getDefaultItems ? getDefaultItems(items) : items;
      return { results: wrap(base.slice(0, visibleCount)), totalAvailable: base.length };
    }
    if (searchItems) {
      const full = searchItems(items, currentQuery);
      return { results: wrap(full.slice(0, visibleCount)), totalAvailable: full.length };
    }
    const full = smartSearch(items, currentQuery, fields, { limit: items.length });
    return { results: full.slice(0, visibleCount), totalAvailable: full.length };
  }, [items, currentQuery, fields, visibleCount, getDefaultItems, searchItems]);

  const hasMore = results.length < totalAvailable;
  const showMoreIndex = results.length;

  const handleShowMore = React.useCallback(() => {
    setVisibleCount((c) => Math.min(c + initialCap, totalAvailable));
  }, [initialCap, totalAvailable]);

  const closeDesktopDropdown = React.useCallback(
    () => {
      setInputValue("");
      setOpen(false);
      setActiveIndex(-1);
    },
    [],
  );

  // Scroll active item into view
  React.useEffect(() => {
    if (activeIndex < 0 || !listRef.current) return;
    const activeEl = listRef.current.children[activeIndex] as HTMLElement;
    activeEl?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  // ── Handlers ───────────────────────────────────────────────────

  const openDropdown = React.useCallback(() => {
    if (disabled) return;
    if (isMobile) {
      setMobileOpen(true);
      setMobileInputValue("");
      setActiveIndex(-1);
    } else {
      setOpen(true);
      setActiveIndex(-1);
    }
  }, [disabled, isMobile]);

  const closeDropdown = React.useCallback(() => {
    if (isMobile) {
      setMobileOpen(false);
      setMobileInputValue("");
    } else {
      closeDesktopDropdown();
    }
    setActiveIndex(-1);
  }, [isMobile, closeDesktopDropdown]);

  const handleDesktopOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (disabled || isMobile) return;
      if (nextOpen) {
        setOpen(true);
        setActiveIndex(-1);
      } else {
        closeDesktopDropdown();
      }
    },
    [disabled, isMobile, closeDesktopDropdown],
  );

  const selectItem = React.useCallback(
    (item: T) => {
      const key = getKey(item);
      onChange(key);
      setInputValue("");
      setMobileInputValue("");
      closeDropdown();
    },
    [getKey, onChange, closeDropdown],
  );

  const handleInputChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
      setActiveIndex(-1);
      if (!open) {
        setOpen(true);
        setActiveIndex(-1);
      }
    },
    [open],
  );

  const handleInputFocus = React.useCallback(() => {
    if (isMobile) return;
    if (pointerFocusRef.current) {
      pointerFocusRef.current = false;
      return;
    }

    // Keyboard focus should not open the menu. If a selected value exists,
    // pre-fill and select it so the user can immediately type to replace it.
    if (selectedItem) {
      const prefill = getSearchValue ? getSearchValue(selectedItem) : getDisplayLabel(selectedItem);
      setInputValue(prefill);
      requestAnimationFrame(() => inputRef.current?.select());
    }
  }, [isMobile, selectedItem, getSearchValue, getDisplayLabel]);

  const handleInputClick = React.useCallback(() => {
    if (isMobile) {
      openDropdown();
      return;
    }
    if (open) {
      setInputValue("");
      closeDropdown();
    } else {
      openDropdown();
    }
  }, [isMobile, open, openDropdown, closeDropdown]);

  const handleInputBlur = React.useCallback(
    () => {
      if (isMobile) return;
      onBlur?.();
    },
    [isMobile, onBlur],
  );

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (disabled || isMobile) return;

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          if (!open) {
            setOpen(true);
            setActiveIndex(-1);
          } else {
            const maxIndex = hasMore ? results.length : results.length - 1;
            setActiveIndex((prev) => (prev < maxIndex ? prev + 1 : prev));
          }
          break;
        }
        case "ArrowUp": {
          e.preventDefault();
          if (!open) {
            setOpen(true);
            setActiveIndex(-1);
          } else {
            setActiveIndex((prev) => (prev > 0 ? prev - 1 : 0));
          }
          break;
        }
        case "Enter": {
          e.preventDefault();
          if (open && activeIndex === showMoreIndex && hasMore) {
            handleShowMore();
            break;
          }
          if (open && results.length > 0) {
            const idx = activeIndex >= 0 && activeIndex < results.length ? activeIndex : 0;
            selectItem(results[idx].item);
            // setTimeout(0) rather than requestAnimationFrame — rAF fires before
            // useEffect callbacks, so fields whose disabled state is derived in
            // an effect would still be stale at query time.
            setTimeout(() => {
              const container = inputRef.current?.closest("form") ?? inputRef.current?.closest("[role='dialog']");
              if (container) {
                const focusable = Array.from(
                  container.querySelectorAll<HTMLElement>(
                    'input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]):not([tabindex="-1"])',
                  ),
                );
                const curIdx = focusable.indexOf(inputRef.current!);
                focusable[curIdx + 1]?.focus();
              }
            }, 0);
          }
          break;
        }
        case "Escape": {
          if (open) {
            e.preventDefault();
            e.stopPropagation();
            closeDesktopDropdown();
          }
          break;
        }
        case "Tab": {
          if (open && results.length > 0) {
            const idx = activeIndex >= 0 && activeIndex < results.length ? activeIndex : 0;
            selectItem(results[idx].item);
          } else {
            closeDesktopDropdown();
          }
          break;
        }
      }
    },
    [disabled, isMobile, open, activeIndex, results, hasMore, showMoreIndex, handleShowMore, selectItem, closeDesktopDropdown],
  );

  const handleClear = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      onClear?.();
      setInputValue("");
      closeDropdown();
      inputRef.current?.blur();
    },
    [onClear, closeDropdown],
  );

  const handleOptionMouseDown = React.useCallback(
    (e: React.MouseEvent, item: T) => {
      e.preventDefault();
      selectItem(item);
      inputRef.current?.blur();
    },
    [selectItem],
  );

  const handleMobileItemClick = React.useCallback(
    (item: T) => {
      selectItem(item);
    },
    [selectItem],
  );

  // ── Default item renderer ──────────────────────────────────────

  const defaultRenderItem = React.useCallback(
    (item: T, _highlighted: boolean, _matches: Map<number, MatchRange[]>, tokens?: string[]) => {
      const label = getDisplayLabel(item);
      if (!tokens?.length) return label;

      const ranges = highlightTokensInText(label, tokens);
      if (!ranges.length) return label;

      const parts = splitByMatches(label, ranges);
      return (
        <>
          {parts.map((part, i) =>
            part.highlighted ? (
              <mark key={i} className="bg-primary/20 text-inherit rounded-sm">
                {part.text}
              </mark>
            ) : (
              <span key={i}>{part.text}</span>
            ),
          )}
        </>
      );
    },
    [getDisplayLabel],
  );

  const renderer = renderItem ?? defaultRenderItem;

  // ── Render helpers ──────────────────────────────────────────────

  const isSearching = inputValue.trim().length > 0;

  // Desktop options are rendered through the shadcn-style Popover wrapper so
  // dialog bodies can stay clipped/scrollable while the floating list can
  // escape that clipping. Keep this popover non-modal: the parent dialog owns
  // focus trapping and scroll locking.
  const renderOptions = (mobile: boolean) => (
    <ul
      ref={listRef}
      role="listbox"
      data-dialog-portal-interactive="true"
      className={cn(
        mobile
          ? "flex-1 overflow-y-auto overscroll-contain"
          : cn(
              "z-[70] overflow-y-auto overflow-x-hidden overscroll-contain pointer-events-auto outline-none",
              "rounded-lg border border-border bg-popover text-popover-foreground shadow-md",
            ),
      )}
    >
      {results.length === 0 ? (
        <li className="px-3 py-6 text-center text-sm text-muted-foreground">
          No results found.
        </li>
      ) : (
        <>
          {results.map((result, index) => {
            const key = getKey(result.item);
            const isActive = index === activeIndex;
            const isSelected = key === value;

            return (
              <li
                key={key}
                id={`combobox-option-${index}`}
                role="option"
                aria-selected={isSelected}
                onMouseDown={mobile ? undefined : (e) => handleOptionMouseDown(e, result.item)}
                onClick={mobile ? () => handleMobileItemClick(result.item) : undefined}
                onMouseEnter={mobile ? undefined : () => setActiveIndex(index)}
                className={cn(
                  "relative flex cursor-pointer select-none items-center px-3 text-sm overflow-hidden",
                  "text-foreground",
                  mobile ? "py-3 min-h-[44px] active:bg-accent" : "py-2",
                  isActive && !mobile && "bg-accent text-accent-foreground",
                  !isActive && !mobile && "hover:bg-accent/50",
                  isSelected && mobile && "bg-accent/30",
                )}
              >
                <div className="min-w-0 flex-1 overflow-hidden">
                  {renderer(result.item, isActive, result.matches, result.matchedTokens)}
                </div>
              </li>
            );
          })}
          {hasMore && (
            <li
              id={`combobox-option-${showMoreIndex}`}
              role="option"
              aria-selected={false}
              onMouseDown={mobile ? undefined : (e) => { e.preventDefault(); handleShowMore(); }}
              onClick={mobile ? () => handleShowMore() : undefined}
              onMouseEnter={mobile ? undefined : () => setActiveIndex(showMoreIndex)}
              className={cn(
                "cursor-pointer select-none px-3 text-center text-sm border-t border-border",
                mobile ? "py-3 min-h-[44px] active:bg-accent" : "py-2",
                activeIndex === showMoreIndex && !mobile
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:bg-accent/50",
              )}
            >
              Show more ({totalAvailable - results.length} remaining)
            </li>
          )}
        </>
      )}
    </ul>
  );

  const dropdownContentStyle: React.CSSProperties = {
    width: dropdownWidth ?? "var(--radix-popover-trigger-width)",
    maxHeight: "min(320px, var(--radix-popover-content-available-height))",
  };

  return (
    <Popover
      modal={false}
      open={!isMobile && open}
      onOpenChange={handleDesktopOpenChange}
    >
      <PopoverAnchor asChild>
        <div ref={containerRef} className={cn("relative min-w-0", className)}>
      {/* Input trigger */}
      <div
        className={cn(
          "relative flex min-w-0 items-center gap-1 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground",
          renderSelected && "min-h-[58px]",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-1",
          disabled && "opacity-50 cursor-not-allowed",
          error && "border-destructive bg-destructive/10",
        )}
      >
        {/* Rich selected display */}
        {renderSelected && selectedItem && (
          <div
            className={cn(
              "flex-1 min-w-0 overflow-hidden",
              (open || isSearching) ? "invisible" : "cursor-default",
            )}
            onPointerDown={() => { pointerFocusRef.current = true; }}
            onClick={() => {
              if (isMobile) {
                openDropdown();
              } else {
                inputRef.current?.focus();
                openDropdown();
              }
            }}
          >
            {renderSelected(selectedItem)}
          </div>
        )}

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open || mobileOpen}
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-activedescendant={
            activeIndex >= 0 ? `combobox-option-${activeIndex}` : undefined
          }
          aria-invalid={!!error}
          disabled={disabled}
          readOnly={isMobile}
          placeholder={
            disabled && disabledMessage
              ? disabledMessage
              : open
                ? placeholder
                : selectedItem
                  ? displayText
                  : placeholder
          }
          value={isMobile ? "" : (isSearching || open ? inputValue : "")}
          onChange={isMobile ? undefined : handleInputChange}
          onPointerDown={() => { pointerFocusRef.current = true; }}
          onFocus={handleInputFocus}
          onClick={handleInputClick}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          className={cn(
            "min-w-0 overflow-hidden text-ellipsis bg-transparent outline-none placeholder:text-muted-foreground",
            renderSelected && selectedItem
              ? cn("absolute inset-0 px-3 py-2", clearable && "pr-9", !open && !isSearching && "opacity-0 pointer-events-none")
              : cn("flex-1", !isSearching && !open && selectedItem && "placeholder:text-foreground"),
            isMobile && "cursor-pointer",
          )}
          autoComplete="off"
        />

        {/* Clear button */}
        {clearable && selectedItem && !disabled && (
          <button
            type="button"
            tabIndex={-1}
            onMouseDown={handleClear}
            className="flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-sm text-muted-foreground hover:text-foreground sm:min-h-0 sm:min-w-0"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Desktop dropdown — shadcn Popover owns portal, positioning, outside click, and collision handling. */}
      {!isMobile && open && !disabled && (
        <PopoverContent
          asChild
          align="start"
          sideOffset={8}
          collisionPadding={12}
          className="z-[70] w-auto p-0"
          style={dropdownContentStyle}
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onEscapeKeyDown={(event) => event.stopPropagation()}
        >
          {renderOptions(false)}
        </PopoverContent>
      )}

      {/* Mobile full-screen sheet — nested Radix Dialog so it layers properly with parent dialog */}
      {isMobile && (
        <DialogPrimitive.Root open={mobileOpen} onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            setMobileOpen(false);
            setMobileInputValue("");
            setActiveIndex(-1);
            onBlur?.();
          }
        }}>
          <DialogPrimitive.Portal>
            <DialogPrimitive.Content
              data-dialog-portal-interactive="true"
              className={cn(
                "fixed inset-x-0 top-0 z-50 flex flex-col bg-background overflow-hidden pointer-events-auto",
                "data-[state=open]:animate-in data-[state=closed]:animate-out",
                "data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom",
                "duration-200",
              )}
              style={{ height: "100dvh" }}
              onOpenAutoFocus={(e) => e.preventDefault()}
              onPointerDownOutside={(e) => e.preventDefault()}
              onInteractOutside={(e) => e.preventDefault()}
            >
              <DialogPrimitive.Title className="sr-only">
                {placeholder}
              </DialogPrimitive.Title>
              <DialogPrimitive.Description className="sr-only">
                Search and select an option
              </DialogPrimitive.Description>

              {/* Mobile header */}
              <div className="flex items-center gap-2 border-b border-border px-3 py-2">
                <input
                  type="text"
                  placeholder={placeholder}
                  value={mobileInputValue}
                  onChange={(e) => {
                    setMobileInputValue(e.target.value);
                    setActiveIndex(-1);
                  }}
                  className="flex-1 h-10 bg-secondary rounded-md px-3 text-sm text-foreground placeholder:text-muted-foreground outline-none"
                  autoComplete="off"
                />
                <DialogPrimitive.Close className="shrink-0 h-10 w-10 flex items-center justify-center rounded-md text-muted-foreground hover:text-foreground">
                  <X className="h-5 w-5" />
                </DialogPrimitive.Close>
              </div>

              {/* Mobile results */}
              {renderOptions(true)}
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
      )}

      {/* Error message */}
      {error && (
        <p className="mt-1 text-xs text-destructive">{error}</p>
      )}
        </div>
      </PopoverAnchor>
    </Popover>
  );
}

export default ComboBox;
